import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, CheckCircle, Clock, ThumbsUp, Leaf, Star,
  ArrowRight, Home as HomeIcon, Trash2, Sofa, Package,
  Building2, Zap
} from 'lucide-react';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const StatCard = ({ number, label }) => (
  <div className="text-center">
    <div className="text-4xl font-black text-green-400 mb-1">{number}</div>
    <div className="text-gray-300 text-sm font-medium">{label}</div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-md card-hover animate-on-scroll">
    <div className="bg-green-100 rounded-xl p-3 flex-shrink-0">
      <Icon size={26} className="text-green-600" />
    </div>
    <div>
      <h3 className="font-bold text-gray-900 text-base mb-1">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

const ServiceCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white rounded-2xl p-6 shadow-md card-hover border border-gray-100 animate-on-scroll">
    <div className="bg-green-600 rounded-xl p-3 w-fit mb-4">
      <Icon size={24} className="text-white" />
    </div>
    <h3 className="font-bold text-gray-900 text-lg mb-3">{title}</h3>
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="text-gray-500 text-sm flex items-center gap-2">
          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const TestimonialCard = ({ name, location, text, stars = 5 }) => (
  <div className="bg-white rounded-2xl p-6 shadow-md animate-on-scroll">
    <div className="flex gap-1 mb-3">
      {[...Array(stars)].map((_, i) => (
        <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
      ))}
    </div>
    <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{text}"</p>
    <div>
      <div className="font-bold text-gray-900 text-sm">{name}</div>
      <div className="text-green-600 text-xs">{location}</div>
    </div>
  </div>
);

export default function Home() {
  useScrollAnimation();

  return (
    <div>
      {/* HERO */}
      <section className="hero-gradient min-h-[90vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-green-400 blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-green-300 blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-600/30 border border-green-500/40 rounded-full px-4 py-2 mb-6">
                <Zap size={14} className="text-green-400" />
                <span className="text-green-300 text-sm font-semibold">Same Day Service Available</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
                LAWLOR<br />
                <span className="text-green-400">RUBBISH</span><br />
                REMOVAL
              </h1>
              <p className="text-green-200 text-xl font-semibold mb-3">— MAN WITH A VAN —</p>
              <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-lg">
                Fast, reliable, and affordable rubbish removal across Dublin, Meath, Louth & Kildare. <strong className="text-white">You don't lift a finger — we do the work!</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="tel:0852730131"
                  className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200 text-lg shadow-lg shadow-green-900/40 pulse-green"
                >
                  <Phone size={22} />
                  085 273 0131
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-200 text-lg"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
              <div className="flex flex-wrap gap-4">
                {['No Hidden Fees', 'Free Estimates', 'Weekend Service', 'We Recycle'].map((badge) => (
                  <div key={badge} className="flex items-center gap-2 text-green-200 text-sm">
                    <CheckCircle size={16} className="text-green-400" />
                    {badge}
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-green-600/20 backdrop-blur-sm border border-green-500/30 rounded-3xl p-8">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-green-500 rounded-full px-5 py-2 mb-4">
                      <span className="text-white font-black text-lg">✓ FAST · RELIABLE · AFFORDABLE</span>
                    </div>
                    <h2 className="text-white text-2xl font-bold">We Take Care Of:</h2>
                  </div>
                  <div className="space-y-4">
                    {[
                      { icon: HomeIcon, title: 'Property Clearance', sub: 'Houses · Flats · Offices' },
                      { icon: Trash2, title: 'General Rubbish Removal', sub: 'Bagged Waste · Bulky Items' },
                      { icon: Sofa, title: 'Furniture & Appliance Removal', sub: 'Old Furniture · White Goods' },
                    ].map(({ icon: Icon, title, sub }) => (
                      <div key={title} className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                        <div className="bg-green-500 rounded-lg p-2">
                          <Icon size={20} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-bold text-sm">{title}</div>
                          <div className="text-green-200 text-xs">{sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-3 border-t border-green-500/30 pt-6">
                    <div className="text-center">
                      <Clock size={20} className="text-green-400 mx-auto mb-1" />
                      <div className="text-white text-xs font-semibold">Same Day Service</div>
                    </div>
                    <div className="text-center">
                      <ThumbsUp size={20} className="text-green-400 mx-auto mb-1" />
                      <div className="text-white text-xs font-semibold">Honest Quotes</div>
                    </div>
                    <div className="text-center">
                      <Leaf size={20} className="text-green-400 mx-auto mb-1" />
                      <div className="text-white text-xs font-semibold">We Recycle</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-green-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard number="500+" label="Jobs Completed" />
            <StatCard number="4" label="Counties Served" />
            <StatCard number="100%" label="Customer Satisfaction" />
            <StatCard number="7" label="Days a Week" />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">The Lawlor Difference</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We're locally owned and operated, delivering fast and honest service every time.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Clock} title="Quick Response & Same Day Service" desc="We respond fast. Same day and weekend service available — because we know rubbish can't always wait." />
            <FeatureCard icon={ThumbsUp} title="Fair Prices, No Hidden Fees" desc="Honest quotes with no surprises. We charge fairly and transparently for every job, big or small." />
            <FeatureCard icon={Leaf} title="Responsible Eco-Friendly Disposal" desc="We separate and recycle wherever possible, ensuring your waste is handled in an environmentally responsible way." />
            <FeatureCard icon={CheckCircle} title="Fully Licensed & Insured" desc="Professionally licensed waste carriers. You're in safe, trusted hands with every job we undertake." />
            <FeatureCard icon={Star} title="5-Star Rated Service" desc="Our customers love our work. Consistently rated 5 stars for reliability, professionalism, and results." />
            <FeatureCard icon={Zap} title="No Job Too Big or Small" desc="From a single item to a full property clearance — if you need it gone, we'll get it done." />
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Our Services</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Comprehensive rubbish removal solutions for homes and businesses across Ireland.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <ServiceCard
              icon={HomeIcon}
              title="Property Clearance"
              items={['House Clean Out', 'Flat & Apartment Clearance', 'Office Clearance', 'Estate Clean Out']}
            />
            <ServiceCard
              icon={Trash2}
              title="General Rubbish Removal"
              items={['Bagged Waste Collection', 'Bulky Item Removal', 'Garden Waste', 'Hoarder Clear Out']}
            />
            <ServiceCard
              icon={Sofa}
              title="Furniture & Appliances"
              items={['Old Furniture Removal', 'White Goods & Fridges', 'Appliance Disposal', 'Hot Tub Removal']}
            />
            <ServiceCard
              icon={Building2}
              title="Commercial Services"
              items={['Commercial Rubbish Removal', 'Commercial Building Clearance', 'Shed Removal', 'Light Demolition']}
            />
            <ServiceCard
              icon={Package}
              title="Specialist Removals"
              items={['Light Demolition Work', 'Shed Dismantling & Removal', 'Construction Waste', 'Garage Clearance']}
            />
            <div className="bg-green-600 rounded-2xl p-6 flex flex-col justify-between animate-on-scroll">
              <div>
                <h3 className="text-white font-black text-xl mb-3">Not Sure What You Need?</h3>
                <p className="text-green-100 text-sm leading-relaxed mb-6">
                  Give us a call for a free, no-obligation quote. We'll assess your needs and give you an honest price.
                </p>
              </div>
              <div className="space-y-3">
                <a
                  href="tel:0852730131"
                  className="flex items-center justify-center gap-2 bg-white text-green-700 font-bold px-5 py-3 rounded-xl hover:bg-green-50 transition-colors"
                >
                  <Phone size={18} />
                  085 273 0131
                </a>
                <Link
                  to="/services"
                  className="flex items-center justify-center gap-2 border border-white/50 text-white font-semibold px-5 py-3 rounded-xl hover:bg-white/10 transition-colors text-sm"
                >
                  View All Services <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Customer Reviews</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">What Our Customers Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              name="Sarah M."
              location="Dublin"
              text="Chris was brilliant — came the same day I called and cleared the entire shed quickly and efficiently. Very fair price and left everything spotless. Highly recommend!"
            />
            <TestimonialCard
              name="James O'Brien"
              location="Meath"
              text="Used Lawlor Rubbish Removal for a full house clearance. They were professional, fast and the price was exactly as quoted with no hidden extras. Will definitely use again."
            />
            <TestimonialCard
              name="Patricia K."
              location="Louth"
              text="Fantastic service from start to finish. Responded quickly, gave a great price and removed all the old furniture and appliances without any fuss. 5 stars all the way!"
            />
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="hero-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Ready to Get Rid of That Rubbish?
          </h2>
          <p className="text-green-200 text-lg mb-8">
            Call now for a free, no-obligation quote. Same day service available across Dublin, Meath, Louth & Kildare.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0852730131"
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-black px-10 py-5 rounded-2xl transition-all text-xl shadow-lg"
            >
              <Phone size={24} />
              085 273 0131
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold px-10 py-5 rounded-2xl transition-all text-lg"
            >
              Send a Message <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
